<template>
  <v-container fluid>
    <!-- component title -->
    <h1 v-html="$t('labels.applications-dash')"></h1>
    <v-text-field
      prepend-icon="$sl-search"
      single-line
      hide-details
      @input="debouncedOnChange"
      v-model="search"
      :label="$t('application.search-label')"
      append-outer-icon="$sl-filter"
      @click:append-outer="showFilter = !showFilter"
    ></v-text-field>
    <template v-if="hasDepartments($route.params.id)">
      <MessageBox v-if="!isAdministrator" class="my-5" type="info">
        <p>{{ $t('company.application-messagebox') }} {{ getCurrentUserDepartments($route.params.id, isAdministrator, false) }}</p>
      </MessageBox>
      <v-tabs v-model="tab" :fixed-tabs="$vuetify.breakpoint.xsOnly" :centered="$vuetify.breakpoint.xsOnly">
        <v-tab key="1"> {{ $t('labels.all') }}</v-tab>
        <v-tab key="2">{{ $t('company.sections') }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item key="1">
          <ApplicationsTable :items="applications"></ApplicationsTable>
          <EndlessScroll @fetchMore="listenScroll" v-if="$vuetify.breakpoint.smAndDown" />
        </v-tab-item>
        <v-tab-item key="2">
          <ReviewApplicationsDepartments :items="applications"></ReviewApplicationsDepartments>
          <EndlessScroll @fetchMore="listenScroll" v-if="$vuetify.breakpoint.smAndDown" />
           </v-tab-item>
      </v-tabs-items>
    </template>
    <template v-else>
      <ApplicationsTable :items="applications"></ApplicationsTable>
      <!-- Used to scroll applications on mobile versions -->
      <EndlessScroll @fetchMore="listenScroll" v-if="$vuetify.breakpoint.smAndDown" />
    </template>
  </v-container>
</template>

<script>
import API from '@/services/api.js'
import {mapGetters, mapActions} from 'vuex'
import find from 'lodash/find'
import uniq from 'lodash/uniq'
import debounce from 'lodash/debounce'
import concat from 'lodash/concat'
import MessageBox from '@/components/Common/MessageBox.vue'
import ActionButton from '@/components/Common/ActionButton'
import ApplicationsTable from '@/components/Application/ApplicationsTable'
import ReviewApplicationsDepartments from '@/components/Application/ReviewApplicationsDepartments'
import EndlessScroll from '@/components/Common/EndlessScroll'
import {Icons} from '@/components/mixins'
import {CompanyRoles} from '@/components/mixins'

// @group Company
// ReviewApplications component
export default {
  name: 'ReviewApplications',

  mixins: [Icons, CompanyRoles],

  components: {
    ActionButton,
    EndlessScroll,
    MessageBox,
    ApplicationsTable,
    ReviewApplicationsDepartments
  },

  data() {
    return {
      tab: 0,
      applications: [],
      options: {},
      showScroll: false,
      totalRows: 0,
      startAt: 0,
      sortBy: '',
      sortDesc: '',
      itemsPerPage: 500,
      gotAll: false,
      isLoading: false,
      appStatusIcon: '',
      search: '',
      showFilter: ['md', 'lg', 'xl'].includes(this.$vuetify.breakpoint.name)
    }
  },

  computed: {
    ...mapGetters('currentuser', ['currentUser', 'isAdministrator']),
    ...mapGetters('basic', ['basicStates', 'getStateNameById']),
    // DebouncedOnChange calles when an input is triggered on the field
    // @arg The argument is a function this.searchChanged
    debouncedOnChange() {
      return debounce(this.doSearch, 500)
    },

    /**
     * @vuese
     * Used to get company id from the route param id
     */
    forCompanyId() {
      return this.$route.params.id
    }
  },

  created() {
    console.log('ReviewApplication -- created')
    //this.fetchListData()
    this.debouncedOnChange()
  },

  watch: {
    /**
     * @vuese
     * Used to watch currentUser and then call debouncedOnChange method
     */
    currentUser: {
      handler: function () {
        this.debouncedOnChange()
      },
      deep: true
    },
    options: {
      handler() {        
        this.applications = []
        this.gotAll = false
        const {sortBy, sortDesc, page, itemsPerPage} = this.options
        this.sortBy = sortBy
        this.sortDesc = sortDesc
        this.itemsPerPage = itemsPerPage
        this.startAt = (page - 1) * itemsPerPage
        this.fetchListData()
      },
      deep: true
    }
  },

  methods: {
    ...mapActions('application', ['setSelectedApplication']),
    find: find,
    /**
     * @vuese
     * Used to do search and set the default values
     */
    doSearch() {
      this.applications = []
      this.gotAll = false
      this.startAt = 0
      this.fetchListData()
    },
    /**
     * @vuese
     * Used to list applications
     * @arg the params are the search criteries
     */
    fetchListData() {
      if (this.isLoading || this.gotAll) {
        return
      }

      this.isLoading = true

      API.listApplications({
        startAt: this.startAt,
        sortBy: this.sortBy[0],
        sortDesc: this.sortDesc[0],
        search: this.search,
        pageSize: this.itemsPerPage,
        type: 'received',
        companyId: this.forCompanyId
      })
        .then(response => {               
          let emp = this.currentUser.employments.find(x => x.companyId == this.$route.params.id) 
          let appArray = []          
          if (this.hasDepartments(this.$route.params.id) == true) {            
            response.data.rows.forEach(r => {              
              if(this.isAdministrator){
                appArray.push(r)
              }
              else if(r.departmentId && r.departmentId>0){
                if (this.hasDepartmentId(emp, r.departmentId, false) == true) {     
                  appArray.push(r)
                }
              }
              else if (r.departments && r.departments.length > 0) {
                r.departments.forEach(d => {                                     
                  if (r.instructorId==emp.id || this.isAdministrator == true) {
                    appArray.push(r)
                  } else if (this.hasDepartmentId(emp, d.id, false) == true) {                                        
                    appArray.push(r)
                    
                  }                  
                })
              }
              else {
                appArray.push(r)
              }
            })
            appArray = uniq(appArray, 'id')
          } else {            
            appArray = response.data.rows
          }          
          this.totalRows = appArray.length
          this.gotAll = appArray.length === 0
          this.isLoading = false
          if (this.startAt === 0) {
            this.applications = appArray
          } else {
            
            this.applications = concat(this.applications, appArray)
            //this.applications = uniq(this.applications, 'id')
            this.applications = uniqBy(this.applications, 'id')            
          }
          this.applications = uniq(this.applications, 'id')          
        })
        .catch(error => {
          this.isLoading = false
          console.log('There was an error:', error.response)
        })
    },
    //Fetch the list on scroll
    // @arg The argument the length of the reports list from the first call
    listenScroll(val) {
      if (!this.gotAll) {
        this.itemsPerPage = 25
        this.startAt = this.applications.length + 1
        this.fetchListData()
      }
    },
    /**
     * @vuese
     * Used to show an application
     * @arg The argument is the application id
     */
    showApplication(id) {
      API.getApplication(id)
        .then(response => {
          this.setSelectedApplication(response.data)
          this.$nextTick(() => {
            this.$router.push({
              name: 'AdminApplicationStart',
              params: {id: this.$route.params.id, application_id: id}
            })
          })
        })
        .catch(error => {
          console.log('There was an error getApplication:', error)
        })
    }
    // fetchListOptionsData() {
    //   API.listApplications({
    //     startAt: this.startAt,
    //     sortBy: this.sortBy[0],
    //     sortDesc: this.sortDesc[0],
    //     search: this.search,
    //     pageSize: this.itemsPerPage,
    //     type: 'received'
    //   })
    //     .then(response => {
    //       this.totalRows = response.data.count
    //       this.applications = response.data.rows
    //     })
    //     .catch(error => {
    //       this.isLoading = false
    //       console.log('There was an error:', error.response)
    //     })
    // }
  }
}
</script>

<style lang="scss">
</style>